import { Alert, Button, Card, Field, Input, Modal, Select, Textarea } from "@aman/design-system";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, asApiError, type ApiError } from "../api/client";
import { useApiQuery } from "../api/hooks";
import { ApiErrorAlert, DataTable, LoadingTable, PageHeader, SearchBar, StatusBadge, useFilteredRows } from "../components/common";
import { isRecord, listFromPayload, objectFromPayload, stringValue, type AnyRecord } from "../types";

type LicenseAction = "suspend" | "resume" | "revoke";

function recordId(record: AnyRecord): string {
  return stringValue(record["id"], stringValue(record["publicId"], stringValue(record["public_id"])));
}

function productId(record: AnyRecord): string {
  return stringValue(record["productId"], stringValue(record["product_id"]));
}

function planId(record: AnyRecord, plans: AnyRecord[]): string {
  const explicit = stringValue(record["planId"], stringValue(record["plan_id"]));
  if (explicit !== "") return explicit;
  const key = stringValue(record["planKey"], stringValue(record["plan_key"]));
  const product = productId(record);
  const match = plans.find((plan) => stringValue(plan["key"]) === key && (product === "" || productId(plan) === product));
  return match === undefined ? "" : recordId(match);
}

function displayName(record: AnyRecord): string {
  return stringValue(record["name"], stringValue(record["displayName"], stringValue(record["display_name"], recordId(record))));
}

function entitlementOverrides(record: AnyRecord): AnyRecord {
  const value = record["entitlementOverrides"] ?? record["entitlement_overrides"];
  return isRecord(value) ? value : {};
}

export function LicensesPage() {
  const { t } = useTranslation();
  const query = useApiQuery<unknown>("/licenses");
  const productsQuery = useApiQuery<unknown>("/products");
  const plansQuery = useApiQuery<unknown>("/plans");
  const customersQuery = useApiQuery<unknown>("/customers");
  const resellersQuery = useApiQuery<unknown>("/resellers");
  const [search, setSearch] = useState("");
  const [issueOpen, setIssueOpen] = useState(new URLSearchParams(location.search).has("issue"));
  const [settingsFor, setSettingsFor] = useState<AnyRecord>();
  const [confirmAction, setConfirmAction] = useState<{ record: AnyRecord; action: LicenseAction }>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const [issuedKey, setIssuedKey] = useState<string>();
  const [copied, setCopied] = useState(false);
  const rows = useFilteredRows(listFromPayload(query.data), search);
  const products = listFromPayload(productsQuery.data);
  const plans = listFromPayload(plansQuery.data);
  const customers = listFromPayload(customersQuery.data);
  const resellers = listFromPayload(resellersQuery.data);

  const renew = async (record: AnyRecord): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await api.post(`/licenses/${encodeURIComponent(recordId(record))}/renew`, { reason: "admin_renewal", durationDays: 365 });
      await query.reload();
    } catch (caught) {
      setError(asApiError(caught));
    } finally {
      setBusy(false);
    }
  };

  const lifecycleAction = async (record: AnyRecord, action: LicenseAction, reason: string): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await api.post(`/licenses/${encodeURIComponent(recordId(record))}/${action}`, { reason });
      setConfirmAction(undefined);
      await query.reload();
    } catch (caught) {
      setError(asApiError(caught));
    } finally {
      setBusy(false);
    }
  };

  const updateSettings = async (record: AnyRecord, payload: Record<string, unknown>): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await api.patch(`/licenses/${encodeURIComponent(recordId(record))}`, payload);
      setSettingsFor(undefined);
      await query.reload();
    } catch (caught) {
      setError(asApiError(caught));
    } finally {
      setBusy(false);
    }
  };

  const issue = async (payload: Record<string, unknown>): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      const result = await api.post<unknown>("/licenses/issue", payload);
      const object = objectFromPayload(result);
      setIssuedKey(stringValue(object["licenseKey"], stringValue(object["key"])));
      await query.reload();
    } catch (caught) {
      setError(asApiError(caught));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (): Promise<void> => {
    if (issuedKey === undefined) return;
    try {
      await navigator.clipboard.writeText(issuedKey);
      setCopied(true);
    } catch (caught) {
      setError(asApiError(caught));
    }
  };

  return (
    <div className="page-stack">
      <PageHeader
        title={t("pages.licenses.title")}
        subtitle={t("pages.licenses.subtitle")}
        actions={<Button type="button" onClick={() => { setError(undefined); setIssuedKey(undefined); setCopied(false); setIssueOpen(true); }}>＋ {t("pages.licenses.issue")}</Button>}
      />
      <ApiErrorAlert error={query.error ?? productsQuery.error ?? plansQuery.error ?? customersQuery.error ?? resellersQuery.error} onRetry={() => void query.reload()} />
      <ApiErrorAlert error={error} />
      <Card bodyClassName="card-body--flush">
        <SearchBar value={search} onChange={setSearch} onRefresh={() => void query.reload()} loading={query.loading} />
        {query.loading && query.data === undefined ? <LoadingTable columns={7} /> : (
          <DataTable
            rows={rows}
            columns={[
              { key: "key", label: t("common.key"), render: (row) => <code>{stringValue(row["keyPrefix"], stringValue(row["key_prefix"], "AMAN"))}••••{stringValue(row["keyLast4"], stringValue(row["key_last4"]))}</code> },
              { key: "product", label: t("pages.licenses.product"), render: (row) => stringValue(row["productName"], stringValue(row["product_id"])) },
              { key: "customer", label: t("pages.licenses.customer"), render: (row) => stringValue(row["customerName"], stringValue(row["customer_id"])) },
              { key: "plan", label: t("pages.licenses.plan"), render: (row) => stringValue(row["planName"], stringValue(row["planKey"], stringValue(row["plan_key"], "—"))) },
              { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
              { key: "expiresAt", label: t("common.date"), render: (row) => stringValue(row["expiresAt"], stringValue(row["expires_at"], "—")) },
              { key: "maxDevices", label: t("pages.licenses.maxDevices"), render: (row) => stringValue(row["maxDevices"], stringValue(row["max_devices"], "—")) },
            ]}
            actions={(row) => {
              const status = stringValue(row["status"]).toLowerCase();
              const terminal = status === "revoked";
              return <>
                {!terminal ? <Button type="button" size="small" variant="secondary" busy={busy} onClick={() => void renew(row)}>{t("pages.licenses.renew")}</Button> : null}
                {status === "active" ? <Button type="button" size="small" variant="secondary" disabled={busy} onClick={() => { setError(undefined); setConfirmAction({ record: row, action: "suspend" }); }}>{t("pages.licenses.suspend")}</Button> : null}
                {status === "suspended" ? <Button type="button" size="small" variant="secondary" disabled={busy} onClick={() => { setError(undefined); setConfirmAction({ record: row, action: "resume" }); }}>{t("pages.licenses.resume")}</Button> : null}
                {!terminal ? <Button type="button" size="small" variant="secondary" disabled={busy} onClick={() => { setError(undefined); setSettingsFor(row); }}>{t("pages.licenses.settings")}</Button> : null}
                {!terminal ? <Button type="button" size="small" variant="danger" disabled={busy} onClick={() => { setError(undefined); setConfirmAction({ record: row, action: "revoke" }); }}>{t("pages.licenses.revoke")}</Button> : null}
              </>;
            }}
          />
        )}
      </Card>
      <IssueLicenseModal
        open={issueOpen}
        busy={busy}
        error={error}
        products={products}
        plans={plans}
        customers={customers}
        resellers={resellers}
        issuedKey={issuedKey}
        copied={copied}
        onCopy={() => void copy()}
        onClose={() => setIssueOpen(false)}
        onIssue={issue}
      />
      {settingsFor !== undefined ? (
        <LicenseSettingsModal
          record={settingsFor}
          plans={plans}
          busy={busy}
          error={error}
          onClose={() => setSettingsFor(undefined)}
          onSave={(payload) => updateSettings(settingsFor, payload)}
        />
      ) : null}
      {confirmAction !== undefined ? (
        <LicenseActionModal
          action={confirmAction.action}
          record={confirmAction.record}
          busy={busy}
          error={error}
          onClose={() => setConfirmAction(undefined)}
          onConfirm={(reason) => lifecycleAction(confirmAction.record, confirmAction.action, reason)}
        />
      ) : null}
    </div>
  );
}

function IssueLicenseModal({ open, busy, error, products, plans, customers, resellers, issuedKey, copied, onCopy, onClose, onIssue }: {
  open: boolean;
  busy: boolean;
  error: ApiError | undefined;
  products: AnyRecord[];
  plans: AnyRecord[];
  customers: AnyRecord[];
  resellers: AnyRecord[];
  issuedKey: string | undefined;
  copied: boolean;
  onCopy: () => void;
  onClose: () => void;
  onIssue: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [resellerId, setResellerId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void onIssue({
      productId: selectedProductId,
      planId: selectedPlanId,
      customerId,
      ...(resellerId !== "" ? { resellerId } : {}),
      ...(expiresAt !== "" ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
    });
  };
  const option = (record: AnyRecord) => {
    const id = recordId(record);
    return <option value={id} key={id}>{displayName(record)}</option>;
  };
  return (
    <Modal open={open} title={t("pages.licenses.issue")} onClose={onClose}>
      <ApiErrorAlert error={error} />
      {issuedKey !== undefined && issuedKey !== "" ? (
        <Alert tone="success" title={t("pages.licenses.issuedKey")}>
          <div className="issued-key"><code dir="ltr">{issuedKey}</code><Button type="button" size="small" variant="secondary" onClick={onCopy}>{copied ? t("common.copied") : t("pages.licenses.copyKey")}</Button></div>
        </Alert>
      ) : (
        <form className="form-grid" onSubmit={submit}>
          <Field label={t("pages.licenses.product")}>
            <Select required value={selectedProductId} onChange={(event) => { setSelectedProductId(event.target.value); setSelectedPlanId(""); }}>
              <option value="">{t("common.select")}</option>{products.map(option)}
            </Select>
          </Field>
          <Field label={t("pages.licenses.plan")}>
            <Select required value={selectedPlanId} onChange={(event) => setSelectedPlanId(event.target.value)}>
              <option value="">{t("common.select")}</option>{plans.filter((plan) => selectedProductId === "" || productId(plan) === selectedProductId).map(option)}
            </Select>
          </Field>
          <Field label={t("pages.licenses.customer")}>
            <Select required value={customerId} onChange={(event) => setCustomerId(event.target.value)}>
              <option value="">{t("common.select")}</option>{customers.map(option)}
            </Select>
          </Field>
          <Field label={t("nav.resellers")}>
            <Select value={resellerId} onChange={(event) => setResellerId(event.target.value)}>
              <option value="">{t("common.none")}</option>{resellers.map(option)}
            </Select>
          </Field>
          <Field label={t("pages.licenses.expiresAt")}><Input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} /></Field>
          <Button type="submit" busy={busy}>{t("pages.licenses.issue")}</Button>
        </form>
      )}
    </Modal>
  );
}

function LicenseSettingsModal({ record, plans, busy, error, onClose, onSave }: {
  record: AnyRecord;
  plans: AnyRecord[];
  busy: boolean;
  error: ApiError | undefined;
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const currentProductId = productId(record);
  const matchingPlans = useMemo(
    () => plans.filter((plan) => currentProductId === "" || productId(plan) === currentProductId),
    [currentProductId, plans],
  );
  const initialPlanId = planId(record, matchingPlans);
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlanId);
  const [maxDevices, setMaxDevices] = useState(stringValue(record["maxDevices"], stringValue(record["max_devices"], "1")));
  const [overridesJson, setOverridesJson] = useState(JSON.stringify(entitlementOverrides(record), null, 2));
  const [validationError, setValidationError] = useState<string>();
  const selectedPlan = matchingPlans.find((plan) => recordId(plan) === selectedPlanId);
  const planEntitlements = isRecord(selectedPlan?.["entitlements"]) ? selectedPlan["entitlements"] : {};

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const parsedMaxDevices = Number(maxDevices);
    if (!Number.isInteger(parsedMaxDevices) || parsedMaxDevices < 0) {
      setValidationError(t("pages.licenses.maxDevicesInvalid"));
      return;
    }
    let parsedOverrides: unknown;
    try {
      parsedOverrides = JSON.parse(overridesJson);
    } catch {
      setValidationError(t("pages.licenses.entitlementsJsonInvalid"));
      return;
    }
    if (!isRecord(parsedOverrides)) {
      setValidationError(t("pages.licenses.entitlementsObjectRequired"));
      return;
    }
    setValidationError(undefined);
    void onSave({ planId: selectedPlanId, maxDevices: parsedMaxDevices, entitlements: parsedOverrides });
  };

  return (
    <Modal open title={t("pages.licenses.settingsTitle")} onClose={onClose}>
      <ApiErrorAlert error={error} />
      <form className="form-grid" onSubmit={submit}>
        <Field label={t("pages.licenses.plan")}>
          <Select required value={selectedPlanId} onChange={(event) => setSelectedPlanId(event.target.value)}>
            <option value="">{t("common.select")}</option>
            {matchingPlans.map((plan) => <option value={recordId(plan)} key={recordId(plan)}>{displayName(plan)} ({stringValue(plan["key"])})</option>)}
          </Select>
        </Field>
        <Field label={t("pages.licenses.maxDevices")} hint={t("pages.licenses.maxDevicesHint")}>
          <Input required type="number" min={0} step={1} value={maxDevices} onChange={(event) => setMaxDevices(event.target.value)} />
        </Field>
        <Field className="form-grid__full" label={t("pages.licenses.planEntitlements")} hint={t("pages.licenses.planEntitlementsHint")}>
          <Textarea readOnly rows={6} dir="ltr" spellCheck={false} value={JSON.stringify(planEntitlements, null, 2)} />
        </Field>
        <Field className="form-grid__full" label={t("pages.licenses.entitlementOverrides")} hint={t("pages.licenses.entitlementOverridesHint")} error={validationError}>
          <Textarea rows={10} dir="ltr" spellCheck={false} value={overridesJson} onChange={(event) => { setOverridesJson(event.target.value); setValidationError(undefined); }} />
        </Field>
        <div className="form-actions form-grid__full">
          <Button type="button" variant="secondary" disabled={busy} onClick={() => { setOverridesJson("{}"); setValidationError(undefined); }}>{t("pages.licenses.usePlanDefaults")}</Button>
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" busy={busy}>{t("common.save")}</Button>
        </div>
      </form>
    </Modal>
  );
}

function LicenseActionModal({ action, record, busy, error, onClose, onConfirm }: {
  action: LicenseAction;
  record: AnyRecord;
  busy: boolean;
  error: ApiError | undefined;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const normalizedReason = reason.trim();
    if (normalizedReason.length < 3) return;
    void onConfirm(normalizedReason);
  };
  const destructive = action === "revoke";
  return (
    <Modal open title={t(`pages.licenses.${action}Title`)} onClose={onClose}>
      <ApiErrorAlert error={error} />
      {destructive ? <Alert tone="warning" title={t("pages.licenses.revokeWarningTitle")}>{t("pages.licenses.revokeWarning")}</Alert> : null}
      <form className="form-grid" onSubmit={submit}>
        <Field className="form-grid__full" label={t("common.reason")} hint={t("pages.licenses.reasonHint")}>
          <Textarea required minLength={3} maxLength={500} rows={4} value={reason} onChange={(event) => setReason(event.target.value)} autoFocus />
        </Field>
        <div className="form-actions form-grid__full">
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" variant={destructive ? "danger" : "primary"} busy={busy} disabled={reason.trim().length < 3}>
            {t(`pages.licenses.${action}`)}
          </Button>
        </div>
      </form>
      <span className="aman-hint">{t("pages.licenses.selectedLicense", { id: recordId(record) })}</span>
    </Modal>
  );
}
